import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import api, { uploadFile, FILE_BASE_URL } from '../../utils/api';
import { getUser } from '../../utils/auth';
import './TherapistFormPage.css';

const TherapistFormPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;
  const user = getUser();

  const [form, setForm] = useState({
    name: '', nameKo: '', nameEn: '', nameZh: '', nameJa: '',
    nameTh: '', nameFr: '', nameDe: '', nameEs: '',
    bioKo: '', bioEn: '', bioZh: '', bioJa: '',
    bioTh: '', bioFr: '', bioDe: '', bioEs: '',
    age: '', experience: '', locationId: user?.role === 'MASTER' ? user.locationId : '',
    rating: '5.0', photo: '', specialtyIds: [],
  });
  const [locations, setLocations] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [locRes, courseRes] = await Promise.all([
          api.get('/locations'),
          api.get('/courses'),
        ]);
        setLocations(locRes.data);
        setCourses(courseRes.data);

        if (isEdit) {
          const res = await api.get(`/therapists/${id}`);
          const t = res.data;
          setForm({
            name: t.name || '', nameKo: t.nameKo || '', nameEn: t.nameEn || '',
            nameZh: t.nameZh || '', nameJa: t.nameJa || '', nameTh: t.nameTh || '',
            nameFr: t.nameFr || '', nameDe: t.nameDe || '', nameEs: t.nameEs || '',
            bioKo: t.bioKo || '', bioEn: t.bioEn || '', bioZh: t.bioZh || '',
            bioJa: t.bioJa || '', bioTh: t.bioTh || '', bioFr: t.bioFr || '',
            bioDe: t.bioDe || '', bioEs: t.bioEs || '',
            age: t.age || '', experience: t.experience || '',
            locationId: t.locationId || '', rating: t.rating || '5.0',
            photo: t.photo || '', specialtyIds: t.specialtyIds || [],
          });
        }
      } catch (error) {
        console.error('Failed to fetch data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, isEdit]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSpecialtyToggle = (courseId) => {
    setForm((prev) => ({
      ...prev,
      specialtyIds: prev.specialtyIds.includes(courseId)
        ? prev.specialtyIds.filter((s) => s !== courseId)
        : [...prev.specialtyIds, courseId],
    }));
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const filename = await uploadFile(file);
      setForm((prev) => ({ ...prev, photo: filename }));
    } catch (error) {
      console.error('Failed to upload photo:', error);
      alert('Failed to upload photo');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        age: parseInt(form.age) || 0,
        experience: parseInt(form.experience) || 0,
        rating: parseFloat(form.rating) || 5.0,
      };
      if (isEdit) {
        await api.put(`/therapists/${id}`, payload);
      } else {
        await api.post('/therapists', payload);
      }
      navigate('/admin/therapists');
    } catch (error) {
      console.error('Failed to save therapist:', error);
      alert('Failed to save therapist');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <AdminLayout><div className="admin-loading">Loading...</div></AdminLayout>;
  }

  return (
    <AdminLayout>
      <div className="therapist-form-page">
        <button className="back-btn" onClick={() => navigate('/admin/therapists')}>
          ← Back to Therapists
        </button>
        <h1>{isEdit ? 'Edit Therapist' : 'Add Therapist'}</h1>

        <form onSubmit={handleSubmit} className="admin-form">
          <div className="form-section">
            <h3>Basic Info</h3>
            <div className="form-grid">
              <div className="form-field">
                <label>English Name *</label>
                <input name="name" value={form.name} onChange={handleChange} required />
              </div>
              <div className="form-field">
                <label>Location *</label>
                <select name="locationId" value={form.locationId} onChange={handleChange} required
                  disabled={user?.role === 'MASTER'}>
                  <option value="">Select location</option>
                  {locations.map((loc) => (
                    <option key={loc.id} value={loc.id}>{loc.nameEn}</option>
                  ))}
                </select>
              </div>
              <div className="form-field">
                <label>Age</label>
                <input type="number" name="age" value={form.age} onChange={handleChange} min="18" max="70" />
              </div>
              <div className="form-field">
                <label>Experience (years)</label>
                <input type="number" name="experience" value={form.experience} onChange={handleChange} min="0" />
              </div>
              <div className="form-field">
                <label>Rating</label>
                <input type="number" name="rating" value={form.rating} onChange={handleChange} min="0" max="5" step="0.1" />
              </div>
              <div className="form-field">
                <label>Photo</label>
                <div className="photo-upload-area">
                  {form.photo && (
                    <div className="photo-preview">
                      <img src={`${FILE_BASE_URL}/${form.photo}`} alt="Preview" />
                    </div>
                  )}
                  <input
                    type="file"
                    ref={fileInputRef}
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    onChange={handlePhotoUpload}
                    style={{ display: 'none' }}
                  />
                  <button
                    type="button"
                    className="upload-btn"
                    onClick={() => fileInputRef.current.click()}
                    disabled={uploading}
                  >
                    {uploading ? 'Uploading...' : form.photo ? 'Change Photo' : 'Upload Photo'}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3>Specialties</h3>
            <div className="specialty-checks">
              {courses.map((course) => (
                <label key={course.id} className={`specialty-check ${form.specialtyIds.includes(course.id) ? 'active' : ''}`}>
                  <input
                    type="checkbox"
                    checked={form.specialtyIds.includes(course.id)}
                    onChange={() => handleSpecialtyToggle(course.id)}
                  />
                  {course.icon} {course.nameEn}
                </label>
              ))}
            </div>
          </div>

          <div className="form-section">
            <h3>Localized Names</h3>
            <div className="form-grid">
              {[
                ['nameKo', 'Korean'], ['nameEn', 'English'], ['nameZh', 'Chinese'], ['nameJa', 'Japanese'],
                ['nameTh', 'Thai'], ['nameFr', 'French'], ['nameDe', 'German'], ['nameEs', 'Spanish'],
              ].map(([field, label]) => (
                <div className="form-field" key={field}>
                  <label>{label}</label>
                  <input name={field} value={form[field]} onChange={handleChange} />
                </div>
              ))}
            </div>
          </div>

          <div className="form-section">
            <h3>Localized Bios</h3>
            <div className="form-grid-full">
              {[
                ['bioKo', 'Korean'], ['bioEn', 'English'], ['bioZh', 'Chinese'], ['bioJa', 'Japanese'],
                ['bioTh', 'Thai'], ['bioFr', 'French'], ['bioDe', 'German'], ['bioEs', 'Spanish'],
              ].map(([field, label]) => (
                <div className="form-field" key={field}>
                  <label>{label}</label>
                  <textarea name={field} value={form[field]} onChange={handleChange} rows="2" />
                </div>
              ))}
            </div>
          </div>

          <div className="form-actions">
            <button type="button" className="cancel-btn" onClick={() => navigate('/admin/therapists')}>Cancel</button>
            <button type="submit" className="save-btn" disabled={saving}>
              {saving ? 'Saving...' : (isEdit ? 'Update' : 'Create')}
            </button>
          </div>
        </form>
      </div>
    </AdminLayout>
  );
};

export default TherapistFormPage;
